import { useState } from 'react';

import HubRoundedIcon from '@mui/icons-material/HubRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import { Stack } from '@mui/material';

import { testAddressProviderConnection } from '../../api/addressSettingsApi';
import { useAddressSettings, useUpdateAddressSettings } from '../../hooks';
import { useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type { AddressFormat, UpdateAddressSettingsRequest } from '../../model/settingsTypes';
import {
  ChipListField,
  FeedbackSnackbar,
  FieldRow,
  MaskedCredentialField,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TestConnectionButton,
  TextInputField,
} from '../components';
import { ADDRESS_FORMAT_OPTIONS } from '../options';

/** Address settings: format, validation and the geocoding/suggestion provider. */
export function AddressSettingsPage() {
  const { canView, canManage, canManageCredentials } = useDomainPermissions('address');
  const query = useAddressSettings();
  const update = useUpdateAddressSettings();
  const form = useSettingsForm(query.data);
  const [providerSecret, setProviderSecret] = useState('');
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const dirty = form.dirty || providerSecret.trim() !== '';

  const reset = () => {
    form.reset();
    setProviderSecret('');
  };

  const handleSave = () => {
    const draft = form.draft;
    if (!draft) {
      return;
    }
    const body: UpdateAddressSettingsRequest = {
      version: draft.version,
      supportedCountries: draft.supportedCountries,
      defaultCountry: draft.defaultCountry,
      addressFormat: draft.addressFormat,
      requiredAddressFields: draft.requiredAddressFields,
      validationEnabled: draft.validationEnabled,
      geocodingEnabled: draft.geocodingEnabled,
      suggestionsEnabled: draft.suggestionsEnabled,
      provider: {
        code: draft.provider?.code ?? null,
        baseUrl: draft.provider?.baseUrl ?? null,
        ...(providerSecret.trim() !== '' ? { secret: providerSecret } : {}),
      },
    };
    update.mutate(body, {
      onSuccess: () => {
        setProviderSecret('');
        success('Address settings saved.');
      },
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title="Address settings"
      subtitle="Address format, validation rules and the geocoding provider."
      icon={<PlaceRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;

        const setProvider = (patch: { code?: string | null; baseUrl?: string | null }) =>
          form.setField('provider', {
            code: draft.provider?.code ?? null,
            baseUrl: draft.provider?.baseUrl ?? null,
            credential: draft.provider?.credential ?? null,
            ...patch,
          });

        return (
          <Stack spacing={2.5}>
            <SettingsCard title="Format & validation" icon={<RuleRoundedIcon />}>
              <ChipListField
                label="Supported countries"
                description="ISO country codes you ship to (e.g. RU)."
                value={draft.supportedCountries}
                onChange={(value) => form.setField('supportedCountries', value)}
                disabled={disabled}
              />
              <TextInputField
                label="Default country"
                value={draft.defaultCountry ?? ''}
                onChange={(value) => form.setField('defaultCountry', value)}
                disabled={disabled}
                placeholder="RU"
              />
              <SelectField
                label="Address format"
                value={draft.addressFormat}
                options={ADDRESS_FORMAT_OPTIONS}
                onChange={(value) => form.setField('addressFormat', value as AddressFormat)}
                disabled={disabled}
              />
              <ChipListField
                label="Required address fields"
                description="Fields a customer must provide."
                value={draft.requiredAddressFields}
                onChange={(value) => form.setField('requiredAddressFields', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Validation"
                description="Validate addresses on entry."
                checked={draft.validationEnabled}
                onChange={(value) => form.setField('validationEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Geocoding"
                checked={draft.geocodingEnabled}
                onChange={(value) => form.setField('geocodingEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Address suggestions"
                checked={draft.suggestionsEnabled}
                onChange={(value) => form.setField('suggestionsEnabled', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard
              title="Geocoding provider"
              description="Configure and verify the address provider (e.g. DaData)."
              icon={<HubRoundedIcon />}
            >
              <TextInputField
                label="Provider code"
                value={draft.provider?.code ?? ''}
                onChange={(value) => setProvider({ code: value || null })}
                disabled={disabled}
                placeholder="dadata"
              />
              <TextInputField
                label="Base URL"
                type="url"
                value={draft.provider?.baseUrl ?? ''}
                onChange={(value) => setProvider({ baseUrl: value || null })}
                disabled={disabled}
                placeholder="https://suggestions.dadata.ru"
              />
              <MaskedCredentialField
                label="Provider credential"
                description="Secret used to authenticate with the provider."
                credential={draft.provider?.credential ?? null}
                secret={providerSecret}
                onSecretChange={setProviderSecret}
                disabled={!canManageCredentials}
              />
              <FieldRow label="Connection" description="Verify the provider is reachable." alignTop>
                <TestConnectionButton run={testAddressProviderConnection} disabled={!canManage} />
              </FieldRow>
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
